import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { ApiKeysTable } from '@/app/(client)/company-settings/-components/apiKeys/ApiKeysTable';
import {
  apiKeysQueryOptions,
  useApiKeysList,
} from '@/app/(client)/company-settings/-components/apiKeys/hooks/useApiKeysList';
import { useCreateApiKey } from '@/app/(client)/company-settings/-components/apiKeys/hooks/useCreateApiKey';
import { useRevokeApiKey } from '@/app/(client)/company-settings/-components/apiKeys/hooks/useRevokeApiKey';
import { PageContainer } from '@/components/shared/PageContainer';
import { ensurePermissions, usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { hasFeature } from '@/lib/utils/features';
import { queryClient } from '@/lib/utils/queryClient';
import { bootstrapSession } from '@/lib/utils/session';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/company-settings/(pages)/api-keys',
)({
  // Replaces a `return null` in the component, which rendered a blank page for
  // a company without the feature and still ran the loader's list request.
  // Both questions belong here: the feature says the company bought
  // self-service keys, `api-key:read` says this user may see the list.
  beforeLoad: async () => {
    const user = useSessionStore.getState().user ?? (await bootstrapSession());
    if (!hasFeature(user, 'self-service-api-key')) {
      throw redirect({ to: '/company-settings/users' });
    }

    const perms = await ensurePermissions();
    if (!perms['api-key'].canRead) {
      throw redirect({ to: '/company-settings/users' });
    }
  },
  loader: () => queryClient.ensureQueryData(apiKeysQueryOptions()),
  component: ApiKeysRoute,
});

function ApiKeysRoute() {
  const { getTranslation: t } = useTranslator({ page: 'apiKeys' });
  const perms = usePermissions()['api-key'];
  const [revokingLabel, setRevokingLabel] = useState<string | null>(null);

  const { records, total, isLoading, table } = useApiKeysList();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  async function handleCreate(label: string): Promise<string> {
    const { data } = await createMutation.mutateAsync({ label });
    void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    return data.companyApiKey;
  }

  async function handleRevoke(label: string) {
    setRevokingLabel(label);
    try {
      await revokeMutation.mutateAsync(label);
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success(t('list.toast.revokeSuccess'));
    } catch {
      toast.error(t('list.toast.revokeError'));
    } finally {
      setRevokingLabel(null);
    }
  }

  return (
    <PageContainer>
      <ApiKeysTable
        records={records}
        total={total}
        isLoading={isLoading}
        table={table}
        onRevoke={handleRevoke}
        isRevoking={revokeMutation.isPending}
        revokingLabel={revokingLabel}
        onCreate={handleCreate}
        isCreating={createMutation.isPending}
        canCreate={perms.canCreate}
        canRevoke={perms.canDelete}
      />
    </PageContainer>
  );
}
